import { useEffect, useMemo, useState } from 'react';
import { useAuth, axiosClient } from '../../auth-lib';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../design-system';
import { buildNavChrome, applyNavBadges } from '../../org-admin/components/navItems';
import { useNotifications } from '../../context/NotificationContext';
import NotificationPanel from '../../components/NotificationPanel';
import AttendanceDayPrompt from '../../components/attendance/AttendanceDayPrompt';


function EmployeeLayout({ children, title, subtitle, headerRight }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');

  const chrome = useMemo(() => buildNavChrome(user), [user]);
  const { counts, countFor } = useNotifications();
  const navItems = useMemo(
    () => applyNavBadges(chrome.items, countFor),
    [chrome.items, counts] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Same as OrgAdminLayout: /me doesn't populate the organization, so fetch
  // its name best-effort for the top bar; on failure the top bar just omits it.
  useEffect(() => {
    const orgId =
      typeof user?.organizationId === 'object' ? user?.organizationId?._id : user?.organizationId;
    if (!orgId) return;
    let cancelled = false;
    axiosClient
      .get(`/organizations/${orgId}`)
      .then((res) => {
        if (!cancelled) setCompanyName(res.data?.name || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AppLayout
      variant="orgadmin"
      brandIcon={chrome.brandIcon}
      brandText={chrome.brandText}
      items={navItems}
      onLogout={handleLogout}
      title={title}
      subtitle={subtitle}
      headerRight={headerRight}
      user={{ name: user?.name, role: user?.role?.name }}
      companyName={companyName}
      notificationCount={counts.total}
      renderNotifications={({ close }) => <NotificationPanel close={close} />}
      searchPlaceholder={chrome.searchPlaceholder}
      onSearch={(query) => {
        if (query) navigate(chrome.searchPath(query));
      }}
    >
      {/* Both shells mount this, because both hold people who clock in. It
          renders nothing at all unless the server says there is something to
          ask — see components/attendance/AttendanceDayPrompt.jsx. */}
      <AttendanceDayPrompt />
      {children}
    </AppLayout>
  );
}

export default EmployeeLayout;
